/**
 * RealtimeProvider - Centralized org-level real-time messaging
 *
 * Auto-subscribes to org channel when user authenticates and handles
 * org-level messages by updating React Query cache. This provides
 * consistent real-time updates across all pages without requiring
 * each component to manage subscriptions individually.
 */

import React, { useEffect } from 'react';
import { useQueryClient, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { RealtimeMessage } from '../services/RealtimeClient';
import { useRealtimeSubscriptions } from '../hooks/useRealtimeUpdates';
import { recordsKeys } from '../hooks/useRecords';

/**
 * Look up a record's customer name from the React Query cache
 */
function getRecordName(queryClient: QueryClient, signalId: string): string {
  // Check record detail cache
  const detail = queryClient.getQueryData(recordsKeys.recordDetail(signalId)) as any;
  if (detail?.customer_name) return detail.customer_name;

  // Scan list caches for the record
  for (const query of queryClient.getQueryCache().getAll()) {
    const key = query.queryKey;
    if (Array.isArray(key) && key[0] === 'records' && key.includes('list')) {
      const data = query.state.data as any;
      const match = data?.records?.find((r: any) => r.id === signalId);
      if (match?.customer_name) return match.customer_name;
    }
  }
  return 'Record';
}

/**
 * Handle org-level messages and update React Query cache
 */
function handleOrgMessage(message: RealtimeMessage, queryClient: QueryClient) {
  const signalId = message.signal_id || message.data?.signal_id;
  const collectionId = message.data?.collection_id;

  console.log('[RealtimeProvider] Received org message:', message.type, { signalId, collectionId });

  switch (message.type) {
    case 'signal.progress':
      if (signalId) {
        const stage = message.data?.stage;

        // Update signal processing status in cache
        updateSignalCache(queryClient, signalId, {
          processing_status: message.data?.processing_status || 'processing',
          _progress: {
            percent: message.data?.percent,
            stage,
            timestamp: message.timestamp
          }
        });

        // Toast on failure
        if (stage === 'failed') {
          const name = getRecordName(queryClient, signalId);
          toast.error(`Classification failed for ${name}`);
        }
      }
      break;

    case 'signal.classified':
      if (signalId) {
        const name = getRecordName(queryClient, signalId);
        toast.success(`${name} classified`, {
          description: 'Classification complete',
          duration: 4000,
        });

        // Invalidate to refetch complete classification data
        queryClient.invalidateQueries({
          queryKey: recordsKeys.recordDetail(signalId)
        });
        queryClient.invalidateQueries({
          queryKey: recordsKeys.all
        });
        // Also invalidate signals queries for v3 compatibility
        queryClient.invalidateQueries({
          queryKey: ['signals', signalId]
        });
      }
      break;

    case 'signal.updated':
    case 'signal.created':
      if (signalId) {
        // Invalidate list queries to show new/updated signals
        queryClient.invalidateQueries({
          queryKey: recordsKeys.all
        });
        queryClient.invalidateQueries({
          queryKey: ['signals']
        });
      }
      break;

    case 'collection.updated':
      if (collectionId) {
        queryClient.invalidateQueries({
          queryKey: recordsKeys.collectionDetail(collectionId)
        });
        queryClient.invalidateQueries({
          queryKey: recordsKeys.all
        });
      }
      break;

    case 'simulation.created':
    case 'simulation.progress':
    case 'simulation.completed':
      if (signalId) {
        queryClient.invalidateQueries({
          queryKey: ['simulations', signalId]
        });
      }
      break;

    case 'evidence.uploaded':
    case 'evidence.processing':
    case 'evidence.completed':
      if (signalId) {
        queryClient.invalidateQueries({
          queryKey: recordsKeys.recordDetail(signalId)
        });
      }
      break;
  }
}

/**
 * Update React Query cache for a specific signal
 */
function updateSignalCache(queryClient: QueryClient, signalId: string, updates: any) {
  // Update record detail cache if it exists
  queryClient.setQueryData(
    recordsKeys.recordDetail(signalId),
    (old: any) => old ? { ...old, ...updates } : undefined
  );

  // Update records list cache if it exists
  // Need to check all possible list query keys
  queryClient.getQueryCache().getAll().forEach(query => {
    const queryKey = query.queryKey;
    // Check if this is a records list query
    if (
      Array.isArray(queryKey) &&
      queryKey[0] === 'records' &&
      queryKey.includes('list')
    ) {
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.records) return old;
        return {
          ...old,
          records: old.records.map((r: any) =>
            r.id === signalId ? { ...r, ...updates } : r
          )
        };
      });
    }
  });
}

interface RealtimeProviderProps {
  children: React.ReactNode;
}

/**
 * RealtimeProvider - Provides centralized org-level real-time messaging
 *
 * Auto-subscribes to org channel when user authenticates and handles
 * org-level messages by updating React Query cache.
 */
export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { subscribe } = useRealtimeSubscriptions();

  // Auto-subscribe to org channel when user is authenticated
  useEffect(() => {
    if (!user?.organization_id) return;

    console.log('[RealtimeProvider] Subscribing to org channel:', user.organization_id);

    const unsubscribe = subscribe('org', user.organization_id, (message: RealtimeMessage) => {
      handleOrgMessage(message, queryClient);
    }, {
      messageTypes: [
        'signal.progress',
        'signal.classified',
        'signal.updated',
        'signal.created',
        'collection.updated',
        'simulation.created',
        'simulation.progress',
        'simulation.completed',
        'evidence.uploaded',
        'evidence.processing',
        'evidence.completed'
      ]
    });

    return unsubscribe;
  }, [user?.organization_id, subscribe, queryClient]);

  return <>{children}</>;
};
